import React, { useRef, useState, useLayoutEffect } from 'react';

// Renders a coach's A.U.T.H.O.R.-structured landing page in one of two modes:
//   - read-only (default): the live preview fed the in-memory draft, and the
//     public /site/:slug route fed the fetched, published data. Both must
//     render byte-for-byte the same markup, which is why this stays one
//     component instead of splitting into "preview" vs "public" variants.
//   - editable (pass editable + the callback props below): the same rendered
//     layout becomes the actual editing surface — click into any text/photo/
//     outcome directly, no separate form. Every optional section's visibility
//     guard changes in this mode (see EMPTY_SET usage below) so a section a
//     coach is actively editing never disappears just because it's currently
//     empty.
const EMPTY_SET = new Set();

const HIDDEN_FILE_INPUT = {
    position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px',
    overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
};

const AUTO_GROW_RESET = {
    display: 'block', width: '100%', resize: 'none', overflow: 'hidden',
    border: 'none', outline: 'none', background: 'transparent',
    padding: 0, margin: 0, boxSizing: 'border-box', fontFamily: 'inherit',
};

// Fully-controlled, auto-growing textarea used for every multi-line editable
// field. Not contentEditable — this app's entire marketing-page data model is
// plain strings (no dangerouslySetInnerHTML anywhere downstream: not here, not
// the public page, not the HTML export's escapeHtml()), and contentEditable
// risks leaking pasted rich text/markup into that model. A controlled
// <textarea> keeps the exact same value/onChange pattern this app already
// uses everywhere, with none of contentEditable's cursor-jump or paste-safety
// pitfalls.
function AutoGrowTextarea({ value, onChange, onBlur, style, placeholder }) {
    const ref = useRef(null);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [value]);
    return (
        <textarea
            ref={ref}
            rows={1}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            style={{ ...AUTO_GROW_RESET, ...style }}
        />
    );
}

// Wraps an editable field with its "AI suggestion" / "Edited" badge. A no-op
// pass-through in read-only mode. "AI suggestion" stays persistently visible
// (the nudge to personalize every drafted section); "Edited" is hover/focus-
// gated — a passive confirmation, not needed at a glance, so a fully-
// personalized page doesn't end up covered in pills.
function FieldWrap({ editable, touched, children, inline }) {
    if (!editable) return children;
    return (
        <div className="mpv-field-wrap" style={inline ? { display: 'inline-block' } : undefined}>
            {children}
            <span className={`mpv-field-badge ${touched ? 'is-edited' : 'is-ai'}`} style={{ ...styles.badge, ...(touched ? styles.badgeEdited : styles.badgeAi) }}>
                {touched ? 'Edited' : 'AI suggestion'}
            </span>
        </div>
    );
}

function HeroPhotoControls({ heroPhoto, onPhotoUpload, onPhotoDelete, uploadingPhoto }) {
    if (!heroPhoto) {
        return (
            <label style={styles.heroPhotoBtnEmpty}>
                {uploadingPhoto ? 'Uploading…' : '+ Add hero photo'}
                <input type="file" accept="image/*" style={HIDDEN_FILE_INPUT} onChange={e => onPhotoUpload(e, 'hero')} disabled={uploadingPhoto} />
            </label>
        );
    }
    return (
        <div className="mpv-hero-photo-controls-filled" style={styles.heroPhotoControls}>
            <label style={styles.heroPhotoBtn}>
                Replace
                <input type="file" accept="image/*" style={HIDDEN_FILE_INPUT} onChange={e => onPhotoUpload(e, 'hero')} disabled={uploadingPhoto} />
            </label>
            <button type="button" style={styles.heroPhotoBtnDanger} onClick={() => onPhotoDelete(heroPhoto.storage_path)} disabled={uploadingPhoto}>
                Remove
            </button>
        </div>
    );
}

export default function MarketingPageView({
    page,
    coachName,
    editable = false,
    touchedFields = EMPTY_SET,
    onFieldChange,
    onFieldBlur,
    onOutcomeChange,
    onOutcomeRemove,
    onAddOutcome,
    wins = [],
    suggestedWins = [],
    onPhotoUpload,
    onPhotoDelete,
    uploadingPhoto = false,
}) {
    const [pickerOpen, setPickerOpen] = useState(false);

    if (!page) return null;

    const heroPhoto = (page.photos || []).find(p => p.placement === 'hero');
    const galleryPhotos = (page.photos || []).filter(p => p.placement !== 'hero');
    const outcomes = page.outcomes || [];

    const availableWins = wins.filter(w => !outcomes.some(o => o.text === w.text));
    const availableStarredQuotes = suggestedWins.filter(s => !outcomes.some(o => o.text === s.quote));

    const change = (field, value) => onFieldChange?.(field, value);
    const blur = (field) => onFieldBlur?.(field);

    return (
        <div style={styles.page}>
            {/* Attention */}
            <div
                className="mpv-hero"
                style={{ ...styles.hero, ...(heroPhoto ? { backgroundImage: `linear-gradient(rgba(26,46,40,0.72), rgba(26,46,40,0.72)), url(${heroPhoto.url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}) }}
            >
                {editable && (
                    <HeroPhotoControls heroPhoto={heroPhoto} onPhotoUpload={onPhotoUpload} onPhotoDelete={onPhotoDelete} uploadingPhoto={uploadingPhoto} />
                )}
                <div style={styles.heroInner}>
                    {coachName && <div style={styles.eyebrow}>{coachName}</div>}
                    <FieldWrap editable={editable} touched={touchedFields.has('headline')}>
                        {editable ? (
                            <AutoGrowTextarea
                                value={page.headline || ''}
                                placeholder="Your headline goes here"
                                onChange={e => change('headline', e.target.value)}
                                onBlur={() => blur('headline')}
                                style={{ ...styles.headline, textAlign: 'center', color: 'inherit' }}
                            />
                        ) : (
                            <h1 style={styles.headline}>{page.headline || 'Your headline goes here'}</h1>
                        )}
                    </FieldWrap>
                    {(editable || page.hook_subtext) && (
                        <FieldWrap editable={editable} touched={touchedFields.has('hook_subtext')}>
                            {editable ? (
                                <AutoGrowTextarea
                                    value={page.hook_subtext || ''}
                                    placeholder="A supporting line under your headline..."
                                    onChange={e => change('hook_subtext', e.target.value)}
                                    onBlur={() => blur('hook_subtext')}
                                    style={{ ...styles.hookSubtext, textAlign: 'center', color: 'inherit' }}
                                />
                            ) : (
                                <p style={styles.hookSubtext}>{page.hook_subtext}</p>
                            )}
                        </FieldWrap>
                    )}
                </div>
            </div>

            {/* Uncover */}
            {(editable || page.pain_point) && (
                <section style={styles.section}>
                    <div style={styles.sectionInner}>
                        <FieldWrap editable={editable} touched={touchedFields.has('pain_point')}>
                            {editable ? (
                                <AutoGrowTextarea
                                    value={page.pain_point || ''}
                                    placeholder="The pain point your clients start with..."
                                    onChange={e => change('pain_point', e.target.value)}
                                    onBlur={() => blur('pain_point')}
                                    style={styles.bodyText}
                                />
                            ) : (
                                <p style={styles.bodyText}>{page.pain_point}</p>
                            )}
                        </FieldWrap>
                    </div>
                </section>
            )}

            {/* Transformation */}
            {(editable || page.transformation) && (
                <section style={{ ...styles.section, background: 'var(--mint)' }}>
                    <div style={styles.sectionInner}>
                        <div style={styles.sectionLabel}>What becomes possible</div>
                        <FieldWrap editable={editable} touched={touchedFields.has('transformation')}>
                            {editable ? (
                                <AutoGrowTextarea
                                    value={page.transformation || ''}
                                    placeholder="What becomes possible for your clients..."
                                    onChange={e => change('transformation', e.target.value)}
                                    onBlur={() => blur('transformation')}
                                    style={styles.bodyTextLarge}
                                />
                            ) : (
                                <p style={styles.bodyTextLarge}>{page.transformation}</p>
                            )}
                        </FieldWrap>
                    </div>
                </section>
            )}

            {/* How */}
            {(editable || page.approach) && (
                <section style={styles.section}>
                    <div style={styles.sectionInner}>
                        <div style={styles.sectionLabel}>How we'll work together</div>
                        <FieldWrap editable={editable} touched={touchedFields.has('approach')}>
                            {editable ? (
                                <AutoGrowTextarea
                                    value={page.approach || ''}
                                    placeholder="How you work with clients..."
                                    onChange={e => change('approach', e.target.value)}
                                    onBlur={() => blur('approach')}
                                    style={styles.bodyText}
                                />
                            ) : (
                                <p style={styles.bodyText}>{page.approach}</p>
                            )}
                        </FieldWrap>
                    </div>
                </section>
            )}

            {/* Services — rendered live from the coach's active Offerings, never
                stored on marketing_pages, so it can't go stale and needs no
                AI-suggestion/Edited-badge/provenance handling like other sections. */}
            {(editable || (page.show_offerings !== false && (page.offerings || []).length > 0)) && (
                <section style={{ ...styles.section, background: 'var(--mint)' }}>
                    <div style={styles.sectionInner}>
                        <div style={styles.sectionLabel}>What I offer</div>
                        {(page.offerings || []).length === 0 ? (
                            editable && <p style={styles.bodyText}>Add Offerings in Settings to list your services here.</p>
                        ) : (
                            <div style={styles.offeringsGrid}>
                                {(page.offerings || []).map(o => (
                                    <div key={o.id} style={styles.offeringCard}>
                                        <div style={styles.offeringName}>{o.name}</div>
                                        {o.description && <p style={styles.offeringDesc}>{o.description}</p>}
                                        {(o.modalities || []).length > 0 && (
                                            <div style={styles.offeringTags}>
                                                {o.modalities.map(m => <span key={m} style={styles.offeringTag}>{m}</span>)}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {(editable || galleryPhotos.length > 0) && (
                <div style={styles.gallery}>
                    {galleryPhotos.map((p, i) => (
                        <div key={i} className="mpv-gallery-photo" style={styles.galleryPhotoWrap}>
                            <img src={p.url} alt={p.caption || ''} style={styles.galleryImg} />
                            {editable && (
                                <button type="button" className="mpv-gallery-delete-btn" style={styles.galleryDeleteBtn} onClick={() => onPhotoDelete(p.storage_path)}>×</button>
                            )}
                        </div>
                    ))}
                    {editable && (
                        <label style={styles.galleryAddTile}>
                            {uploadingPhoto ? 'Uploading…' : '+ Add photo'}
                            <input type="file" accept="image/*" style={HIDDEN_FILE_INPUT} onChange={e => onPhotoUpload(e, 'gallery')} disabled={uploadingPhoto} />
                        </label>
                    )}
                </div>
            )}

            {/* Outcome */}
            {(editable || outcomes.length > 0) && (
                <section style={{ ...styles.section, background: 'var(--forest)' }}>
                    <div style={styles.sectionInner}>
                        <div style={{ ...styles.sectionLabel, color: 'var(--gold-light, #E8CBA9)' }}>Real results</div>
                        <div style={styles.outcomesGrid}>
                            {outcomes.map((o, i) => (
                                <div key={i} className="mpv-quote-block" style={{ ...styles.quoteBlock, position: 'relative' }}>
                                    <div style={styles.quoteMark}>"</div>
                                    {editable ? (
                                        <AutoGrowTextarea
                                            value={o.text}
                                            onChange={e => onOutcomeChange(i, e.target.value)}
                                            onBlur={() => blur(`outcome_${i}`)}
                                            style={{ ...styles.quoteText, color: 'inherit' }}
                                        />
                                    ) : (
                                        <p style={styles.quoteText}>{o.text}</p>
                                    )}
                                    {editable && (
                                        <>
                                            <span style={styles.quoteSourceTag}>
                                                {o.source === 'coach_win' ? 'from Wins Library' : o.source === 'breakthrough_quote' ? 'starred quote' : 'added by you'}
                                            </span>
                                            <button type="button" className="mpv-quote-remove-btn" style={styles.quoteRemoveBtn} onClick={() => onOutcomeRemove(i)}>×</button>
                                        </>
                                    )}
                                </div>
                            ))}
                            {editable && (
                                <div style={styles.quoteAddTile} onClick={() => setPickerOpen(o => !o)}>
                                    + Add outcome
                                </div>
                            )}
                        </div>
                        {editable && pickerOpen && (
                            <div style={styles.outcomePicker}>
                                {availableWins.length === 0 && availableStarredQuotes.length === 0 && (
                                    <div style={styles.outcomePickerEmpty}>No more wins or starred quotes to add — log one from the Wins tab first.</div>
                                )}
                                {availableWins.length > 0 && (
                                    <div style={styles.outcomePickerGroup}>
                                        <div style={styles.outcomePickerLabel}>From your Wins Library</div>
                                        {availableWins.map(w => (
                                            <button key={w.id} type="button" style={styles.outcomePickerBtn} onClick={() => { onAddOutcome(w.text, 'coach_win', w.id); setPickerOpen(false); }}>
                                                + "{w.text.length > 60 ? `${w.text.slice(0, 60)}...` : w.text}"
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {availableStarredQuotes.length > 0 && (
                                    <div style={styles.outcomePickerGroup}>
                                        <div style={styles.outcomePickerLabel}>Starred client quotes</div>
                                        {availableStarredQuotes.map((s, i) => (
                                            <button key={i} type="button" style={styles.outcomePickerBtn} onClick={() => { onAddOutcome(s.quote, 'breakthrough_quote', s.session_id); setPickerOpen(false); }}>
                                                + "{s.quote.length > 60 ? `${s.quote.slice(0, 60)}...` : s.quote}"
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Response */}
            <section style={styles.cta}>
                <div style={styles.sectionInner}>
                    <FieldWrap editable={editable} touched={touchedFields.has('cta_text')} inline>
                        {editable ? (
                            <input
                                type="text"
                                value={page.cta_text || ''}
                                placeholder="Get started"
                                onChange={e => change('cta_text', e.target.value)}
                                onBlur={() => blur('cta_text')}
                                style={{ ...styles.ctaBtn, ...AUTO_GROW_RESET, textAlign: 'center', cursor: 'text', width: 'auto', minWidth: '160px' }}
                            />
                        ) : (
                            <a
                                href={page.cta_url || '#'}
                                style={styles.ctaBtn}
                                onClick={e => { if (!page.cta_url) e.preventDefault(); }}
                            >
                                {page.cta_text || 'Get started'}
                            </a>
                        )}
                    </FieldWrap>
                    {editable && (
                        <FieldWrap editable touched={touchedFields.has('cta_url')} inline>
                            <input
                                type="text"
                                value={page.cta_url || ''}
                                placeholder="https://... (where the button links to)"
                                onChange={e => change('cta_url', e.target.value)}
                                onBlur={() => blur('cta_url')}
                                style={styles.ctaUrlInput}
                            />
                        </FieldWrap>
                    )}
                </div>
            </section>
        </div>
    );
}

const styles = {
    page: { fontFamily: "'DM Sans', sans-serif", color: 'var(--text-dark)', background: 'var(--cream)' },
    hero: { padding: '80px 24px', textAlign: 'center', background: 'var(--forest)', color: 'white', position: 'relative' },
    heroInner: { maxWidth: '720px', margin: '0 auto' },
    eyebrow: { fontSize: '13px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gold-light, #E8CBA9)', marginBottom: '14px' },
    headline: { fontFamily: "'Lora', Georgia, serif", fontSize: 'clamp(28px, 5vw, 46px)', fontWeight: '400', lineHeight: 1.2, margin: '0 0 16px' },
    hookSubtext: { fontSize: '17px', lineHeight: 1.6, color: 'rgba(255,255,255,0.85)', margin: 0 },
    section: { padding: '56px 24px' },
    sectionInner: { maxWidth: '720px', margin: '0 auto' },
    sectionLabel: { fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--sage)', marginBottom: '14px' },
    bodyText: { fontSize: '17px', lineHeight: 1.75, margin: 0 },
    bodyTextLarge: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', lineHeight: 1.6, margin: 0, fontStyle: 'italic' },
    gallery: { display: 'flex', gap: '4px', overflowX: 'auto' },
    galleryPhotoWrap: { position: 'relative', flexShrink: 0 },
    galleryImg: { width: '260px', height: '200px', objectFit: 'cover', flexShrink: 0, display: 'block' },
    galleryDeleteBtn: { position: 'absolute', top: '8px', right: '8px', width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(26,46,40,0.75)', color: 'white', border: 'none', fontSize: '14px', cursor: 'pointer', lineHeight: 1 },
    galleryAddTile: { width: '260px', height: '200px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', border: '2px dashed var(--border)', cursor: 'pointer', color: 'var(--text-soft)', fontSize: '13px', fontWeight: 500, background: 'var(--cream)' },
    outcomesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' },
    offeringsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' },
    offeringCard: { background: 'white', border: '1px solid var(--border)', borderRadius: '10px', padding: '18px' },
    offeringName: { fontFamily: "'Lora', Georgia, serif", fontSize: '17px', color: 'var(--text-dark)', marginBottom: '6px' },
    offeringDesc: { fontSize: '13.5px', lineHeight: 1.6, color: 'var(--text-mid)', margin: '0 0 10px' },
    offeringTags: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
    offeringTag: { fontSize: '11px', padding: '3px 9px', borderRadius: '20px', background: 'var(--mint)', color: 'var(--sage)' },
    quoteBlock: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', padding: '20px' },
    quoteMark: { fontFamily: "'Lora', Georgia, serif", fontSize: '32px', color: 'var(--gold)', lineHeight: 1, marginBottom: '4px' },
    quoteText: { fontSize: '15px', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)', fontStyle: 'italic', margin: 0 },
    quoteSourceTag: { display: 'block', fontSize: '10.5px', color: 'rgba(255,255,255,0.5)', marginTop: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' },
    quoteRemoveBtn: { position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.3)', border: 'none', color: 'white', borderRadius: '50%', width: '20px', height: '20px', fontSize: '13px', cursor: 'pointer', lineHeight: 1 },
    quoteAddTile: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100px', border: '1px dashed rgba(255,255,255,0.3)', borderRadius: '10px', color: 'rgba(255,255,255,0.6)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' },
    outcomePicker: { marginTop: '18px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', padding: '16px' },
    outcomePickerEmpty: { fontSize: '13px', color: 'rgba(255,255,255,0.6)' },
    outcomePickerGroup: { marginBottom: '10px' },
    outcomePickerLabel: { fontSize: '10.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' },
    outcomePickerBtn: { display: 'block', width: '100%', textAlign: 'left', background: 'rgba(255,255,255,0.08)', border: '1px dashed rgba(255,255,255,0.25)', borderRadius: '7px', padding: '8px 12px', fontSize: '12.5px', color: 'rgba(255,255,255,0.85)', cursor: 'pointer', marginBottom: '6px', fontFamily: "'DM Sans', sans-serif" },
    cta: { padding: '64px 24px', textAlign: 'center', background: 'var(--cream)' },
    ctaBtn: { display: 'inline-block', padding: '16px 36px', borderRadius: '10px', background: 'var(--sage)', color: 'white', textDecoration: 'none', fontSize: '16px', fontWeight: '600' },
    ctaUrlInput: { display: 'block', margin: '10px auto 0', fontSize: '12.5px', color: 'var(--text-soft)', textAlign: 'center', border: 'none', outline: 'none', background: 'transparent', width: '100%', maxWidth: '360px' },
    badge: { fontSize: '10.5px', padding: '3px 9px', borderRadius: '20px', fontWeight: '500', whiteSpace: 'nowrap' },
    badgeAi: { background: 'var(--gold-pale)', color: 'var(--gold)' },
    badgeEdited: { background: 'var(--mint)', color: 'var(--sage)' },
    heroPhotoBtnEmpty: { position: 'absolute', bottom: '16px', right: '16px', padding: '8px 14px', borderRadius: '20px', background: 'rgba(255,255,255,0.15)', border: '1px dashed rgba(255,255,255,0.5)', color: 'white', fontSize: '13px', fontWeight: 500, cursor: 'pointer', backdropFilter: 'blur(2px)' },
    heroPhotoControls: { position: 'absolute', bottom: '16px', right: '16px', display: 'flex', gap: '8px' },
    heroPhotoBtn: { padding: '8px 14px', borderRadius: '20px', background: 'rgba(255,255,255,0.9)', color: 'var(--text-dark)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' },
    heroPhotoBtnDanger: { padding: '8px 14px', borderRadius: '20px', background: 'rgba(194,94,94,0.85)', border: 'none', color: 'white', fontSize: '13px', fontWeight: 500, cursor: 'pointer' },
};
